import { useRef, useState, type ChangeEvent } from 'react';

interface VoiceTextInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

export const VoiceTextInput = ({ label, value, onChange }: VoiceTextInputProps) => {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  const isSupported = typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const start = () => {
    if (!isSupported) return;
    const SpeechRecognitionAPI = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognitionAPI();
    recognition.lang = 'mr-IN';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event: any) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      onChange(transcript);
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    setListening(true);
    recognition.start();
  };

  const stop = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
    }
    setListening(false);
  };

  return (
    <div className={'flex flex-col'}>
      <label className={'mb-1 text-xs font-medium text-gray-600'}>{label}</label>
      <div className={'relative'}>
        <input
          type={'text'}
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          className={'w-full rounded border border-gray-300 px-2 py-1.5 pr-9 text-sm'}
        />
        {isSupported && (
          <button
            type={'button'}
            aria-label={'Hold to speak'}
            className={`absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 ${listening ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            onPointerDown={start}
            onPointerUp={stop}
            onPointerLeave={stop}
          >
            <svg xmlns={'http://www.w3.org/2000/svg'} width={16} height={16} viewBox={'0 0 24 24'} fill={'none'} stroke={'currentColor'} strokeWidth={2} strokeLinecap={'round'} strokeLinejoin={'round'}>
              <path d={'M12 2a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3Z'} />
              <path d={'M19 10v2a7 7 0 0 1-14 0v-2'} />
              <line x1={12} y1={19} x2={12} y2={22} />
              <line x1={8} y1={22} x2={16} y2={22} />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};
